import { useAppTheme } from "@memorang/ui";
import type { IconType } from "@memorang/ui";
import Box from "@memorang/ui/src/components/Box";
import {
	type ActionId,
	type ActionImpl,
	KBarResults,
	VisualState,
	useKBar,
	useMatches,
} from "kbar";
import { Fragment, forwardRef, useMemo } from "react";
import type { View } from "react-native";
import { List, Text } from "react-native-paper";

const ResultItem = forwardRef<
	View,
	{
		action: ActionImpl;
		active: boolean;
		currentRootActionId: ActionId;
	}
>(({ action, active, currentRootActionId }, ref) => {
	const theme = useAppTheme();
	const { query } = useKBar();
	const ancestors = useMemo(() => {
		if (!currentRootActionId) {
			return action.ancestors;
		}
		const index = action.ancestors.findIndex(
			(ancestor) => ancestor.id === currentRootActionId,
		);
		// +1 removes the currentRootAction; e.g.
		// if we are on the "Set theme" parent action,
		// the UI should not display "Set theme… > Dark"
		// but rather just "Dark"
		return action.ancestors.slice(index + 1);
	}, [action.ancestors, currentRootActionId]);

	const text = useMemo(
		() => (
			<Box flexDirection="row" alignItems="center">
				{ancestors.map((ancestor) => (
					<Fragment key={ancestor.id}>
						<Text
							variant="bodyMedium"
							style={{
								marginRight: 8,
								color: theme.colors.onSurfaceVariant,
							}}
						>
							{ancestor.name}
						</Text>
						<Text
							variant="bodyMedium"
							style={{
								marginRight: 8,
								color: theme.colors.onSurfaceVariant,
							}}
						>
							›
						</Text>
					</Fragment>
				))}
				<Text variant="bodyMedium">{action.name}</Text>
			</Box>
		),
		[action.name, ancestors, theme.colors.onSurfaceVariant],
	);

	return (
		<List.Item
			ref={ref}
			title={text}
			description={action.subtitle}
			left={
				typeof action.icon === "string"
					? (props) => (
							<List.Icon
								{...props}
								icon={action.icon as IconType}
								color={theme.colors.onSurfaceVariant}
							/>
						)
					: undefined
			}
			right={
				action.shortcut && action.shortcut.length > 0
					? () => (
							<Box
								flexDirection="row"
								gap={4}
								alignItems="center"
								style={{ paddingRight: 8 }}
							>
								{action.shortcut?.map((sc) => (
									<Box
										key={sc}
										style={{
											paddingVertical: 4,
											paddingHorizontal: 6,
											backgroundColor: theme.colors.surfaceVariant,
											borderRadius: 4,
											minWidth: 20,
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<Text
											variant="labelSmall"
											style={{
												color: theme.colors.onSurfaceVariant,
											}}
										>
											{sc}
										</Text>
									</Box>
								))}
							</Box>
						)
					: undefined
			}
			onPress={() => {
				if (action.perform) {
					action.perform(action);
					query.setVisualState(VisualState.hidden);
				}
			}}
			style={{
				backgroundColor: active
					? theme.colors.secondaryContainer
					: "transparent",
				paddingVertical: 8,
			}}
			titleStyle={{
				paddingVertical: 0,
			}}
		/>
	);
});

export default function Results() {
	const { results, rootActionId } = useMatches();
	const theme = useAppTheme();

	return (
		<Box>
			<KBarResults
				items={results}
				onRender={({ item, active }) => {
					return typeof item === "string" ? (
						<List.Section
							title={item}
							titleStyle={{
								fontSize: 12,
								lineHeight: 36,
								color: theme.colors.onSurfaceVariant,
							}}
						>
							<Box />
						</List.Section>
					) : (
						<ResultItem
							action={item}
							active={active}
							currentRootActionId={rootActionId || ""}
						/>
					);
				}}
			/>
		</Box>
	);
}
